import { useEffect, useState } from 'react';
import type { EnlacePersonal, Invitado } from '../../../api/ops/portalRrpp';
import { ErrorApi } from '../../../api/problema';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { usePermisos } from '../../../permisos/permisos';
import { Contador } from '../../../componentes/Contador';
import { Bloque, Botones, Caja, Dato, TarjetaEnlace } from '../comun';
import { codigoDe, useAvisarError } from '../uso';
import { cuandoEs, diaHora, estadoInvitado, hora, nombreRrpp, opsDe, ORIGEN, PERMISOS, sinProtocolo } from '../textos';
import type { ContextoListas } from './ApartadoListas';

/**
 * Ficha del invitado (10.9; `?invitado=` la abre, destino del aviso `listas.pase_no_entregado`, G791): contacto, lista,
 * quién lo apuntó, el pase (`pass_state`) y la llegada (`arrived_at`, G788); su enlace personal de Instagram (solo con
 * Instagram conectado, G517); cambiar nombre y acompañantes con `If-Match`; reenviar el pase (`listPasses` y
 * `resendPass`, G329); retirar con motivo. Retirado por él mismo con el enlace del aviso de privacidad (G755) y sin
 * contacto (cliente suprimido, G518).
 */
export function FichaInvitado({ ctx, invitadoId }: { ctx: ContextoListas; invitadoId: string }) {
    const { props, evento, listas } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [leido, setLeido] = useState<{ g: Invitado; etag: string } | null>(null);
    const [enlace, setEnlace] = useState<EnlacePersonal | null>(null);
    const [nombre, setNombre] = useState('');
    const [acomp, setAcomp] = useState(0);
    const [ocupado, setOcupado] = useState(false);
    const [ventana, setVentana] = useState<'retirar' | 'reenviar' | null>(null);
    const [motivo, setMotivo] = useState('');
    const [probado, setProbado] = useState(false);
    const [vuelta, setVuelta] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getGuest(negocio, invitadoId)
            .then((r) => {
                if (!vivo) {
                    return;
                }
                setLeido({ g: r.datos, etag: r.version ?? '' });
                setNombre(r.datos.display_name);
                setAcomp(r.datos.companions);
                if (r.datos.status === 'active' && r.datos.contact_channel === 'instagram' && permisos.puede(PERMISOS.enlacesVer) && ctx.instagram !== false) {
                    ops.getGuestPersonalLink(negocio, invitadoId)
                        .then((e) => { if (vivo) { setEnlace(e); } })
                        .catch(() => undefined);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    avisarError(e, e instanceof ErrorApi && e.estado === 404 ? 'Este invitado ya no está en la lista.' : 'No se ha podido abrir el invitado.');
                    ctx.abrir(null);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [negocio, invitadoId, vuelta]);

    const g = leido?.g;
    const lista = listas.find((l) => l.id === g?.guest_list_id);
    const terminado = evento?.status === 'ended';
    const editar = g !== undefined && permisos.puede(PERMISOS.invitadosGestionar) && !soloLectura && g.status === 'active' && lista?.status === 'open';
    const reenviar = g !== undefined && permisos.puede(PERMISOS.reenviar) && permisos.puede(PERMISOS.pasesVer) && !soloLectura && g.status === 'active' && g.pass_state === 'delivered' && !terminado;
    const cambiado = g !== undefined && (nombre.trim() !== g.display_name || acomp !== g.companions);
    const e = g === undefined ? null : estadoInvitado(g, terminado);

    const guardar = () => {
        if (leido === null) {
            return;
        }
        if (nombre.trim().length < 2) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        const d = acomp - leido.g.companions;
        ops.updateGuest(negocio, invitadoId, { name: nombre.trim(), companions: acomp }, leido.etag)
            .then((r) => {
                setLeido({ g: r.datos, etag: r.version ?? leido.etag });
                avisar({ tipo: 'ok', texto: d > 0 ? `Guardado. Se emiten ${d === 1 ? 'un pase más' : `${String(d)} pases más`} para sus acompañantes.` : d < 0 ? 'Guardado. Se anulan los pases que sobran y se liberan sus plazas.' : 'Guardado.' });
                ctx.recargar();
            })
            .catch((err: unknown) => {
                if (err instanceof ErrorApi && err.estado === 412) {
                    avisar({ tipo: 'error', texto: 'Alguien ha cambiado este invitado. Lo hemos vuelto a leer.' });
                    setVuelta((v) => v + 1);
                } else if (codigoDe(err) === 'capacity_unavailable') {
                    avisar({ tipo: 'error', texto: 'No quedan plazas para más acompañantes.' });
                } else {
                    avisarError(err);
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const confirmar = () => {
        if (g === undefined) {
            return;
        }
        if (ventana === 'retirar') {
            if (motivo.trim() === '') {
                setProbado(true);
                return;
            }
            setOcupado(true);
            ops.withdrawGuest(negocio, invitadoId, motivo.trim())
                .then((r) => {
                    setLeido((x) => (x === null ? x : { ...x, g: r }));
                    setVentana(null);
                    avisar({ tipo: 'ok', texto: `${g.display_name} retirado. Su pase ya no vale.` });
                    ctx.recargar();
                })
                .catch((err: unknown) => { avisarError(err); })
                .finally(() => { setOcupado(false); });
        } else if (ventana === 'reenviar') {
            setOcupado(true);
            ops.listPasses(negocio, { passable_type: 'invitado', passable_id: invitadoId, status: 'active' })
                .then((p) => (p.data.length === 0 ? Promise.reject(new Error('sin pases')) : Promise.all(p.data.map((x) => ops.resendPass(negocio, x.id)))))
                .then(() => {
                    setVentana(null);
                    avisar({ tipo: 'ok', texto: `Pase reenviado a ${g.display_name}.` });
                })
                .catch((err: unknown) => {
                    avisarError(err, err instanceof ErrorApi ? undefined : 'No tiene ningún pase válido que reenviar.');
                })
                .finally(() => { setOcupado(false); });
        }
    };

    const pase =
        g === undefined
            ? ''
            : g.status !== 'active'
              ? 'Anulado'
              : g.pass_state === 'waiting_instagram'
                ? 'Todavía no: falta que escriba por Instagram'
                : g.pass_state === 'delivered'
                  ? `Enviado ${g.contact_channel === 'instagram' ? 'en el chat' : g.contact_channel === 'whatsapp' ? 'por WhatsApp' : 'por email'}${g.pass_delivered_at == null ? '' : ` · ${diaHora(g.pass_delivered_at)}`}`
                  : 'Sin pase';

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                titulo={g?.display_name ?? 'Invitado'}
                eyebrow={`Invitado · ${lista?.name ?? ''}`}
                subtitulo={`${evento?.name ?? ''} · ${cuandoEs(evento?.starts_at)}`}
                chip={e?.texto}
                chipEstado={e?.chip ?? 'neutral'}
                icono="user"
                accion={editar && cambiado ? 'Guardar' : ''}
                ocupado={ocupado && ventana === null}
                onAccion={guardar}
            >
                {g === undefined || e === null ? (
                    <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                ) : (
                    <div className="rv-ca-cuerpo">
                        <Bloque>
                            {g.customer_id === null ? (
                                <Dato icono="user-x" k="Contacto" v="Sin contacto" sub="Pidió borrar sus datos: ya no tiene ficha de cliente. Su pase sigue valiendo." />
                            ) : (
                                <Dato icono={g.contact_channel === 'instagram' ? 'instagram' : g.contact_channel === 'whatsapp' ? 'message-circle' : 'mail'} k={g.contact_channel === 'instagram' ? 'Instagram' : g.contact_channel === 'whatsapp' ? 'WhatsApp' : 'Email'} v={g.contact_masked ?? (g.contact_channel === 'whatsapp' ? 'Teléfono del formulario web' : 'En su ficha de cliente')} />
                            )}
                            <Dato icono="list-checks" k="Lista" v={lista?.name ?? '—'} />
                            <Dato icono="user" k="Lo apuntó" v={g.member_id == null ? 'La casa (el equipo)' : nombreRrpp(ctx.rrpp.get(g.member_id))} sub={`${ORIGEN[g.origin].charAt(0).toLocaleUpperCase('es')}${ORIGEN[g.origin].slice(1)} · ${diaHora(g.created_at)}`} />
                            <Dato icono="send" k="Pase" v={pase} aviso={g.status === 'active' && g.pass_state === 'waiting_instagram'} />
                            <Dato icono="door-open" k="Llegada" v={g.arrived_at != null ? `Entró a las ${hora(g.arrived_at)}` : terminado ? 'No vino' : 'Todavía no'} />
                            {g.companions > 0 && <Dato icono="users" k="Acompañantes" v={String(g.companions)} />}
                            {(g.answers ?? []).map((a) => (
                                <Dato key={a.label} icono="message-square-text" k={a.label} v={a.value} />
                            ))}
                        </Bloque>
                        {g.status === 'withdrawn' && <Caja icono="user-x">{e.detalle !== '' ? `${e.detalle}.` : 'Retirado de la lista: su pase ya no vale.'}</Caja>}
                        {enlace !== null && enlace.instagram_url !== null && (
                            <Bloque titulo="Su enlace personal" intro="Queda anotado quién lo ve.">
                                <TarjetaEnlace
                                    titulo="Enlace para enviarle por Instagram"
                                    texto={enlace.used === true ? 'Ya escribió con él y tiene su pase. Si lo pierde, puede volver a escribir.' : 'Que su RRPP se lo envíe desde su cuenta. Cuando escriba, le llega el QR.'}
                                    chip={enlace.used === true ? ['Usado', 'confirmed'] : ['Sin usar', 'pending']}
                                    filas={[{ k: 'Instagram', v: sinProtocolo(enlace.instagram_url) }]}
                                />
                            </Bloque>
                        )}
                        {editar && (
                            <Bloque titulo="Cambiar">
                                <Campo etiqueta="Nombre" valor={nombre} onChange={setNombre} max={120} error={probado && nombre.trim().length < 2 ? 'Escribe su nombre.' : null} nombre="nombre-invitado" />
                                <Contador
                                    min={0}
                                    etiqueta="Acompañantes"
                                    valor={acomp}
                                    max={lista.max_companions ?? 0}
                                    ayuda={(lista.max_companions ?? 0) > 0 ? `Hasta ${String(lista.max_companions ?? 0)} en esta lista. Cada uno lleva su pase.` : 'Esta lista no admite acompañantes.'}
                                    onCambio={setAcomp}
                                />
                                <Caja icono="info">Para cambiar su Instagram o su email, retíralo y vuelve a apuntarlo.</Caja>
                            </Bloque>
                        )}
                        {(reenviar || editar) && (
                            <Botones nota={editar ? 'Retirarlo libera su plaza y anula su pase y su enlace personal.' : ''}>
                                {reenviar && <Boton tipo="secundario" icono="send" onClick={() => { setVentana('reenviar'); }}>Reenviar el pase</Boton>}
                                {editar && <Boton tipo="destructivo" icono="user-x" onClick={() => { setMotivo(''); setProbado(false); setVentana('retirar'); }}>Retirar de la lista</Boton>}
                            </Botones>
                        )}
                        {g.status === 'active' && lista !== undefined && lista.status !== 'open' && permisos.puede(PERMISOS.invitadosGestionar) && (
                            <Caja icono="lock">La lista está cerrada: para cambiarlo o retirarlo, reábrela antes.</Caja>
                        )}
                    </div>
                )}
            </PanelLateral>
            <Ventana
                abierta={ventana !== null}
                titulo={ventana === 'retirar' ? `Retirar a ${g?.display_name ?? ''}` : 'Reenviar el pase'}
                texto={
                    ventana === 'retirar'
                        ? `Se libera su plaza${(g?.companions ?? 0) > 0 ? ` (y la de sus ${String(g?.companions ?? 0)} acompañantes)` : ''}, se anula su pase y su enlace personal deja de funcionar.`
                        : g?.contact_channel === 'instagram'
                          ? 'Se lo enviamos otra vez en el chat de Instagram. Es el mismo pase: el anterior sigue valiendo.'
                          : `Se lo enviamos otra vez ${g?.contact_channel === 'whatsapp' ? 'por WhatsApp (gasta un mensaje del paquete Servicio)' : 'por email'}. Es el mismo pase: el anterior sigue valiendo.`
                }
                cancelar="Volver"
                accion={ventana === 'retirar' ? 'Retirar' : 'Reenviar'}
                accionIcono={ventana === 'retirar' ? 'user-x' : 'send'}
                ocupado={ocupado}
                ocupadoTexto={ventana === 'retirar' ? 'Retirando…' : 'Enviando…'}
                movil={movil}
                onAccion={confirmar}
                onCerrar={() => { if (!ocupado) { setVentana(null); } }}
            >
                {ventana === 'retirar' && (
                    <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} max={300} error={probado && motivo.trim() === '' ? 'Escribe el motivo.' : null} ayuda="Por ejemplo: «Ya no puede venir». Queda anotado." />
                )}
            </Ventana>
        </>
    );
}
